import { useEffect, useRef } from 'react';
import { ClarityEvents } from '../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../lib/clarity/utils/captureEvent.utils';
import { GlobalSearchView } from '../globalSearch.types';

interface UseGlobalSearchTrackingParams {
  isOpen: boolean;
  view: GlobalSearchView;
  debouncedQuery: string;
  hasReachedMinLength: boolean;
  suggestedCatalogsCount: number;
}

const useGlobalSearchTracking = ({
  isOpen,
  view,
  debouncedQuery,
  hasReachedMinLength,
  suggestedCatalogsCount,
}: UseGlobalSearchTrackingParams) => {
  const hasTrackedSearchRef = useRef(false);
  const lastNoResultQueryRef = useRef<string | null>(null);
  const hasTrackedSuggestedCatalogsRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedSearchRef.current = false;
      lastNoResultQueryRef.current = null;
      hasTrackedSuggestedCatalogsRef.current = false;
      return;
    }

    if (hasReachedMinLength && !hasTrackedSearchRef.current) {
      hasTrackedSearchRef.current = true;
      captureEvent(ClarityEvents.GLOBAL_SEARCH_SEARCHED);
    }
  }, [isOpen, hasReachedMinLength]);

  useEffect(() => {
    const isShowingSuggestedCatalogs = view === 'empty' && suggestedCatalogsCount > 0;
    if (!isOpen || !isShowingSuggestedCatalogs || hasTrackedSuggestedCatalogsRef.current) {
      return;
    }

    hasTrackedSuggestedCatalogsRef.current = true;
    captureEvent(ClarityEvents.GLOBAL_SEARCH_SHOWED_SUGGESTED_CATALOGS);
  }, [isOpen, view, suggestedCatalogsCount]);

  useEffect(() => {
    if (view !== 'no-result' || lastNoResultQueryRef.current === debouncedQuery) {
      return;
    }

    lastNoResultQueryRef.current = debouncedQuery;
    captureEvent(ClarityEvents.GLOBAL_SEARCH_NO_RESULT);
  }, [view, debouncedQuery]);
};

export default useGlobalSearchTracking;
